import { useCallback, useEffect, useRef, useState } from 'react';
import { pmaImportAPI } from '../services/api';
import { readDiscoveryException } from '../domain/infra/discoveryProgress';

export default function useLabCapacity() {
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ingesting, setIngesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [ingestError, setIngestError] = useState('');
  const [ingestMessage, setIngestMessage] = useState('');
  const [progress, setProgress] = useState(null);
  const [exception, setException] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const runStartedAt = useRef(0);
  const pollRef = useRef(null);
  const settlingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await pmaImportAPI.getLabCapacity();
      setCapacity(res.data || null);
    } catch (err) {
      setCapacity(null);
      setError(err.response?.data?.message || err.message || 'Could not load lab capacity');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyDiscoveryResult = useCallback((data, fallbackName) => {
    if (data?.capacity) setCapacity(data.capacity);
    const probed = Number(data?.probed || data?.ingested || 0);
    const sshOk = Number(data?.sshOk || 0);
    const sshFailed = Number(data?.sshFailed || 0);
    if (data?.sshOk != null) {
      setIngestMessage(
        `Probed ${probed} catalog host${probed === 1 ? '' : 's'} (${sshOk} SSH OK, ${sshFailed} failed).`
      );
      return;
    }
    setIngestMessage(`Ingested ${probed} host${probed === 1 ? '' : 's'} from ${data?.filename || fallbackName}.`);
  }, []);

  const captureFinished = useCallback(
    async (next) => {
      if (settlingRef.current) return;
      settlingRef.current = true;
      stopPolling();
      setProgress(next);
      if (next?.result) applyDiscoveryResult({ ...next.result, capacity: undefined }, 'SMS discovery');
      setElapsedMs(Number(next?.elapsedMs) || 0);
      setRunning(false);
      await reload();
    },
    [applyDiscoveryResult, reload, stopPolling]
  );

  const captureFailed = useCallback(
    (next) => {
      if (settlingRef.current) return;
      settlingRef.current = true;
      stopPolling();
      setProgress(next);
      if (next?.error) {
        setException(next.error);
        setIngestError(next.error.message);
      }
      setElapsedMs(Number(next?.elapsedMs) || 0);
      setRunning(false);
    },
    [stopPolling]
  );

  const pollProgress = useCallback(async () => {
    try {
      const res = await pmaImportAPI.getLabDiscoveryProgress();
      const next = res.data || null;
      if (!next) return;
      setProgress(next);
      if (next.status === 'running') {
        setRunning(true);
        if (next.startedAt) runStartedAt.current = next.startedAt;
        setElapsedMs(Number(next.elapsedMs) || 0);
        return;
      }
      if (next.status === 'finished') {
        await captureFinished(next);
        return;
      }
      if (next.status === 'failed') {
        captureFailed(next);
      }
    } catch {
      // Keep the local timer even if progress polling is not deployed yet.
    }
  }, [captureFailed, captureFinished]);

  const watchProgress = useCallback(() => {
    stopPolling();
    pollProgress();
    pollRef.current = setInterval(pollProgress, 1000);
  }, [pollProgress, stopPolling]);

  const ingestFile = useCallback(
    async (file) => {
      if (!file) return;
      setIngesting(true);
      setIngestError('');
      setIngestMessage('');
      setException(null);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await pmaImportAPI.ingestLabDiscovery(formData);
        applyDiscoveryResult(res.data, file.name);
      } catch (err) {
        const parsed = readDiscoveryException(err);
        setException(parsed);
        setIngestError(parsed.message);
      } finally {
        setIngesting(false);
      }
    },
    [applyDiscoveryResult]
  );

  const runDiscovery = useCallback(async () => {
    setRunning(true);
    setIngestError('');
    setIngestMessage('');
    setException(null);
    setElapsedMs(0);
    runStartedAt.current = Date.now();
    settlingRef.current = false;
    watchProgress();
    try {
      const res = await pmaImportAPI.runLabDiscovery();
      if (res.data) setProgress(res.data);
      if (res.data?.status === 'failed' && res.data.error) {
        captureFailed(res.data);
        return;
      }
      if (res.data?.capacity || (res.status !== 202 && res.data?.probed != null)) {
        applyDiscoveryResult(res.data, 'SMS discovery');
        setProgress((current) => ({
          ...(current || {}),
          status: 'finished',
          elapsedMs: Date.now() - runStartedAt.current,
          result: {
            probed: res.data?.probed,
            sshOk: res.data?.sshOk,
            sshFailed: res.data?.sshFailed,
            discoveredAt: res.data?.discoveredAt,
            hostErrors: res.data?.hostErrors || [],
          },
        }));
        setRunning(false);
        stopPolling();
        return;
      }
      watchProgress();
    } catch (err) {
      const parsed = readDiscoveryException(err);
      if (err.response?.status === 409 && parsed.progress) {
        settlingRef.current = false;
        setIngestMessage(parsed.message);
        setProgress(parsed.progress);
        if (parsed.progress.startedAt) runStartedAt.current = parsed.progress.startedAt;
        watchProgress();
        return;
      }
      setException(parsed);
      setIngestError(parsed.message);
      if (parsed.progress) setProgress(parsed.progress);
      setRunning(false);
      stopPolling();
      if (runStartedAt.current) setElapsedMs(Date.now() - runStartedAt.current);
    }
  }, [applyDiscoveryResult, captureFailed, stopPolling, watchProgress]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await pmaImportAPI.getLabDiscoveryProgress();
        if (cancelled || !res.data) return;
        const next = res.data;
        setProgress(next);
        if (next.status === 'running') {
          settlingRef.current = false;
          setRunning(true);
          runStartedAt.current = next.startedAt || Date.now();
          setElapsedMs(Number(next.elapsedMs) || 0);
          watchProgress();
          return;
        }
        if (next.status === 'failed' && next.error) {
          setException(next.error);
          setIngestError(next.error.message);
          setElapsedMs(Number(next.elapsedMs) || 0);
        }
        if (next.status === 'finished' && next.result) {
          applyDiscoveryResult({ ...next.result, capacity: undefined }, 'SMS discovery');
          setElapsedMs(Number(next.elapsedMs) || 0);
        }
      } catch {
        // Progress endpoint may not be deployed yet.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyDiscoveryResult, watchProgress]);

  useEffect(() => {
    if (!running) return undefined;
    const tick = setInterval(() => {
      if (runStartedAt.current) setElapsedMs(Date.now() - runStartedAt.current);
    }, 250);
    return () => clearInterval(tick);
  }, [running]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  return {
    capacity,
    loading,
    error,
    reload,
    ingesting,
    running,
    ingestError,
    ingestMessage,
    ingestFile,
    runDiscovery,
    progress,
    exception,
    elapsedMs,
  };
}
